/* eslint-disable */

import _Vue from 'vue'
import VueRouter from 'vue-router'
import { HubConnection, HubConnectionBuilder, LogLevel } from '@aspnet/signalr'
import config from '@/config'
import { ISignalRMessage } from '@/utils/types'
import { SignalMessageModule } from '@/store/modules/signalMessages'

export function SignalHubPlugin(Vue: typeof _Vue): void {
    Vue.use(VueRouter)
    // do stuff with options
    const signalHub = new Vue()
    // Provide methods to connect/disconnect from the SignalR hub
    let connection: HubConnection
    let startedPromise: Promise<any> | null = null
    let manuallyClosed = false

    signalHub.startSignalR = (jwtToken: string | Promise<string>) => {
        connection = new HubConnectionBuilder()
            .withUrl(config.$apiUrl + '/hubs/signalhub', { accessTokenFactory: jwtToken ? () => jwtToken : () => '' })
            .configureLogging(LogLevel.Information)
            .build()


        // Forward hub events through the event, so we can listen for them in the Vue components
        connection.off('Message')
        connection.on('Message', (message: ISignalRMessage) => {
            SignalMessageModule.ADD_MESSAGE(message)
            signalHub.$emit('message-received', message)
        })

        connection.off('UpdateProcess')
        connection.on('UpdateProcess', process => {
            signalHub.$emit('process-updated', process)
        })

        connection.off('UpdateWorkflow')
        connection.on('UpdateWorkflow', hotFolder => {
            signalHub.$emit('workflow-updated', hotFolder)
        })

        connection.off('CartUpdated')
        connection.on('CartUpdated', cart => {
            signalHub.$emit('cart-updated', cart)
        })

        connection.off('CartGroupUpdated')
        connection.on('CartGroupUpdated', cart => {
            signalHub.$emit('cartgroup-updated', cart)
        })

        connection.off('AmazonListingUpdated')
        connection.on('AmazonListingUpdated', amazonListing => {
            signalHub.$emit('amazon-listing-updated', amazonListing)
        })

        // Reconnect strategy with exponential backoff and jitter.
        // Bugs previously here:
        //   - 401 retry loop: catch-block fell through to the setTimeout, so a tab with an
        //     expired token kept hammering /negotiate every 5s forever. Fixed by returning
        //     from the 401 branch.
        //   - No backoff: every failure retried at a fixed 5s. After a server recycle every
        //     client hit the server in lockstep (a 'thundering herd' spike). Fixed with
        //     exponential backoff 5s -> 10s -> 20s -> 40s -> 60s (cap) + per-client jitter.
        let retryDelay = 5000
        function start() {
            console.log('Attempting to start SignalR...')
            startedPromise = connection.start()
                .then(() => {
                    retryDelay = 5000 // reset backoff on a successful connect
                })
                .catch(err => {
                    if (err && err.statusCode === 401) {
                        console.log('SignalR unauthorized - stopping')
                        signalHub.stopSignalR()
                        Vue.prototype.$router.push('/login')
                        return // do NOT retry on auth failure
                    }
                    const delay = retryDelay + Math.floor(Math.random() * 1000) // jitter
                    retryDelay = Math.min(retryDelay * 2, 60000)
                    console.log(`SignalR start failed, retrying in ${delay}ms`)
                    return new Promise((resolve, reject) =>
                        setTimeout(
                            () =>
                                start()
                                    .then(resolve)
                                    .catch(reject),
                            delay
                        )
                    )
                })
            return startedPromise
        }
        connection.onclose(() => {
            // Stagger reconnects after a server recycle so all clients don't hit at once.
            if (!manuallyClosed) setTimeout(start, Math.floor(Math.random() * 2000))
        })

        // Start everything
        manuallyClosed = false

        start()
    }

    signalHub.stopSignalR = () => {
        console.log('Stopping SignalR...')
        if (!startedPromise) return

        manuallyClosed = true
        return startedPromise
            .then(() => connection.stop())
            .then(() => {
                startedPromise = null
            })
    }

    Vue.prototype.$signalHub = signalHub
}
